/**
 * Application-wide constants
 * Defines constant values used throughout the application
 */

// Global type definitions for environment variables
declare global {
  interface Window {
    _env_: {
      [key: string]: any;
    };
  }
}

/**
 * Application metadata
 */
export const APP_NAME = 'Enterprise Application';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'Enterprise-grade React TypeScript application';

/**
 * API configuration
 * Timeout can be configured via environment variables:
 * - AIPORTAL_API_TIMEOUT (build-time)
 * - AIPORTAL_APP_API_TIMEOUT (runtime, via window._env_)
 * Default: 60000ms (60 seconds) for better handling of slow APIs
 */
const getApiTimeout = (): number => {
  // Check window._env_ first (runtime config from FastAPI)
  if (typeof window !== 'undefined' && window._env_?.AIPORTAL_APP_API_TIMEOUT) {
    const timeout = parseInt(window._env_.AIPORTAL_APP_API_TIMEOUT, 10);
    if (!isNaN(timeout) && timeout > 0) {
      return timeout;
    }
  }
  
  // Check Vite env variable
  if (window._env_.AIPORTAL_API_TIMEOUT) {
    const timeout = parseInt(window._env_.AIPORTAL_API_TIMEOUT, 10);
    if (!isNaN(timeout) && timeout > 0) {
      return timeout;
    }
  }
  
  // Default: 60 seconds (increased from 30s for better handling of slow APIs)
  return 60000;
};

export const API_TIMEOUT = getApiTimeout();
export const API_RETRY_ATTEMPTS = 3;

/**
 * Pagination defaults
 */
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Date and time formats
 */
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const TIME_FORMAT = 'HH:mm:ss';

/**
 * File upload constraints
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

/**
 * Validation constraints
 */
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
export const MIN_USERNAME_LENGTH = 3;
export const MAX_USERNAME_LENGTH = 50;

/**
 * UI constants
 */
export const DRAWER_WIDTH = 240;
export const APPBAR_HEIGHT = 64;
export const NOTIFICATION_DURATION = 3000; // 3 seconds

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
    AUTH_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
    USER: 'user',
    THEME: 'theme',
    LANGUAGE: 'language',
} as const;

/**
 * Query keys for React Query
 */
export const QUERY_KEYS = {
    AUTH: 'auth',
    USER: 'user',
    USERS: 'users',
    PROFILE: 'profile',
    DASHBOARD: 'dashboard',
    SETTINGS: 'settings',
} as const;

/**
 * HTTP Status codes
 */
export const HTTP_STATUS = {
    OK: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
] as const;

/**
 * User roles
 */
export const USER_ROLES = {
    ADMIN: 'admin',
    USER: 'user',
    GUEST: 'guest',
} as const;

/**
 * Environment variables with defaults
 * Reads from window._env_ (FastAPI runtime) or window._env_ (Vite build-time)
 */
const getEnvValue = (key: string, fallback: string): string => {
    if (typeof window !== 'undefined' && window._env_) {
        const envKey = key.startsWith('AIPORTAL_APP_') ? key : `AIPORTAL_APP_${key}`;
        const value = window._env_[envKey];
        if (value) return value;
    }
    
    const viteKey = key.startsWith('AIPORTAL_') ? key : `AIPORTAL_${key.replace('AIPORTAL_APP_', '')}`;
    return window._env_[viteKey] || fallback;
};

export const ENV = {
    API_BASE_URL: getEnvValue('AIPORTAL_API_BASE_URL', window._env_.AIPORTAL_API_BASE_URL || 'http://localhost:8000'),
    APP_NAME: getEnvValue('AIPORTAL_APP_NAME', window._env_.AIPORTAL_APP_NAME || APP_NAME),
    APP_VERSION: getEnvValue('AIPORTAL_APP_VERSION', window._env_.AIPORTAL_APP_VERSION || APP_VERSION),
} as const;
